import { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { api, formatApiError } from "@/lib/api";
import { T } from "@/constants/testIds";

export default function Contact() {
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "", consent: false });
  const [ask, setAsk] = useState({ email: "", question: "", category: "" });
  const [pray, setPray] = useState({ name: "", email: "", request: "", urgent: false, consent: false });
  const [busy, setBusy] = useState(null);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.consent) return toast.error("Please confirm consent.");
    setBusy("contact");
    try {
      await api.post("/forms/contact", contact);
      toast.success("Thank you. We have received your message.");
      setContact({ name: "", email: "", subject: "", message: "", consent: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const submitAsk = async (e) => {
    e.preventDefault();
    setBusy("ask");
    try {
      await api.post("/forms/ask", ask);
      toast.success("Question received. An elder will respond soon.");
      setAsk({ email: "", question: "", category: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const submitPrayer = async (e) => {
    e.preventDefault();
    if (!pray.consent) return toast.error("Please confirm consent.");
    setBusy("pray");
    try {
      await api.post("/forms/prayer", pray);
      toast.success("Your prayer request is with our team. It will not be shared publicly.");
      setPray({ name: "", email: "", request: "", urgent: false, consent: false });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Get in touch."
        subtitle="We read every message. This site is not a substitute for medical, legal, or emergency counselling — please call local services in an emergency."
      />
      <section className="py-16 sm:py-24">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact form */}
          <form data-testid={T.contactForm} onSubmit={submitContact} className="paper-card p-8">
            <h2 className="heading-3">General Contact</h2>
            <p className="body-copy mt-2 text-ink-muted text-sm">For editorial, partnership, or general enquiries.</p>
            <input data-testid={T.contactName} required placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-6 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <input data-testid={T.contactEmail} required type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-3 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <input data-testid={T.contactSubject} required placeholder="Subject" value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} className="mt-3 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <textarea data-testid={T.contactMessage} required rows={5} placeholder="Message" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className="mt-3 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <label className="mt-3 flex items-start gap-2 text-xs text-ink-muted">
              <input data-testid={T.contactConsent} type="checkbox" checked={contact.consent} onChange={(e) => setContact({ ...contact, consent: e.target.checked })} className="mt-1" />
              <span>I agree to be contacted about this message. My email will not be shared.</span>
            </label>
            <button data-testid={T.contactSubmit} disabled={busy === "contact"} className="btn-primary mt-6 w-full justify-center">
              {busy === "contact" ? "Sending…" : "Send Message"}
            </button>
          </form>

          {/* Ask */}
          <form id="ask" data-testid={T.askForm} onSubmit={submitAsk} className="paper-card p-8">
            <h2 className="heading-3">Ask a Bible Question</h2>
            <p className="body-copy mt-2 text-ink-muted text-sm">An elder or theological reviewer will respond within a few days.</p>
            <input data-testid={T.askEmail} type="email" placeholder="Your email (optional)" value={ask.email} onChange={(e) => setAsk({ ...ask, email: e.target.value })} className="mt-6 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <input data-testid="ask-category-input" placeholder="Category (optional)" value={ask.category} onChange={(e) => setAsk({ ...ask, category: e.target.value })} className="mt-3 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <textarea data-testid={T.askQuestion} required rows={6} placeholder="What is your honest question?" value={ask.question} onChange={(e) => setAsk({ ...ask, question: e.target.value })} className="mt-3 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <button data-testid={T.askSubmit} disabled={busy === "ask"} className="btn-primary mt-6 w-full justify-center">
              {busy === "ask" ? "Sending…" : "Send Question"}
            </button>
          </form>

          {/* Prayer */}
          <form id="prayer" data-testid={T.prayerForm} onSubmit={submitPrayer} className="paper-card p-8 bg-subtle">
            <h2 className="heading-3">Request Prayer</h2>
            <p className="body-copy mt-2 text-ink-muted text-sm">Prayer requests are never displayed publicly.</p>
            <input data-testid={T.prayerName} placeholder="Your name (optional)" value={pray.name} onChange={(e) => setPray({ ...pray, name: e.target.value })} className="mt-6 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <input data-testid="prayer-email-input" type="email" placeholder="Your email (optional)" value={pray.email} onChange={(e) => setPray({ ...pray, email: e.target.value })} className="mt-3 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <textarea data-testid={T.prayerText} required rows={5} placeholder="How can we pray for you?" value={pray.request} onChange={(e) => setPray({ ...pray, request: e.target.value })} className="mt-3 w-full border border-border bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink" />
            <label className="mt-3 flex items-center gap-2 text-xs text-ink-muted">
              <input data-testid={T.prayerUrgent} type="checkbox" checked={pray.urgent} onChange={(e) => setPray({ ...pray, urgent: e.target.checked })} />
              <span>This is urgent.</span>
            </label>
            <label className="mt-2 flex items-start gap-2 text-xs text-ink-muted">
              <input data-testid={T.prayerConsent} type="checkbox" checked={pray.consent} onChange={(e) => setPray({ ...pray, consent: e.target.checked })} className="mt-1" />
              <span>I understand my request will remain private.</span>
            </label>
            <button data-testid={T.prayerSubmit} disabled={busy === "pray"} className="btn-primary mt-6 w-full justify-center">
              {busy === "pray" ? "Sending…" : "Send Prayer Request"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
